import { ResponseCode } from '@/common/request/types';
import { useAppRouter } from '@/common/route/useAppRouter';
import { validateFormat } from '@/common/validator/validatorHelpers';
import { useCrudActions } from '@/common/view/useCrudActions';
import { ROUTE_NAME_HOMEPAGE } from '@/modules/main/router';
import { usePicwordPetList } from '@/modules/picword/pet/model/list';
import { picwordService } from '@/services/picword';
import useVuelidate from '@vuelidate/core';
import { minValue, numeric, required } from '@vuelidate/validators';
import { onMounted, reactive } from 'vue';

export function usePicwordSetting() {
    const router = useAppRouter();
    const crudAction = useCrudActions();

    const { fetchAllPets, picwordPetOptions } = usePicwordPetList();

    const model = reactive({
        totalMap: null,
        maxLevel: null,
        extrawordCapacity: null,
        extrawordGoalToCredit: '',
        welcomePetIds: null,
        heartToPet: null,
        interactiveSeconds: null,
        interactiveCost: null,
        adsHelperMaxAppearanceInLevel: null,
        adsHelperMaxUseInGame: null,
        adsHelperMaxUseInDuration: null,
        adsHelperDurationSecond: null,
        canDelete: false
    });

    const validations = {
        totalMap: { required, numeric, min: minValue(1) },
        maxLevel: { required, numeric, min: minValue(1) },
        interactiveSeconds: { required, numeric, min: minValue(1) },
        interactiveCost: { required, numeric, min: minValue(1) },
        extrawordCapacity: { required, numeric, min: minValue(1) },
        welcomePetIds: {},
        heartToPet: { required, numeric, min: minValue(1) },
        extrawordGoalToCredit: { required, format: validateFormat(/^\d+:\d+(;\d+:\d+)*$/) },
        adsHelperMaxAppearanceInLevel: { required, numeric, min: minValue(0) },
        adsHelperMaxUseInGame: { required, numeric, min: minValue(0) },
        adsHelperMaxUseInDuration: { required, numeric, min: minValue(0) },
        adsHelperDurationSecond: { required, numeric, min: minValue(0) }
    };

    const v$ = useVuelidate(validations, model);

    const fetch = crudAction.executeAction({
        request: picwordService.setting.get,
        onSuccess: ({ data }) => Object.assign(model, data),
        onError: ({ code }) => {
            if (code !== ResponseCode.NothingFound) {
                router.push({ name: ROUTE_NAME_HOMEPAGE });
            }
        }
    });

    const actions = {
        update: crudAction.excuteAndNotify({
            validator: v$,
            request: () =>
                picwordService.setting.update(
                    model.totalMap,
                    model.maxLevel,
                    model.extrawordCapacity,
                    model.heartToPet,
                    model.interactiveSeconds,
                    model.interactiveCost,
                    model.extrawordGoalToCredit,
                    model.adsHelperMaxAppearanceInLevel,
                    model.adsHelperMaxUseInGame,
                    model.adsHelperMaxUseInDuration,
                    model.adsHelperDurationSecond,
                    model.welcomePetIds
                ),
            onFinally: fetch
        }),
        remove: crudAction.confirmAndRemove({
            request: picwordService.setting.remove,
            onFinally: fetch
        })
    };

    onMounted(async () => {
        await fetchAllPets();
        await fetch();
    });

    return { v$, model, picwordPetOptions, actions };
}
